/**
 * FormBuilderProperties - Right panel of the form builder.
 *
 * Shows editable properties for the selected control, section, or the form itself.
 */

import { cn } from '@/shared/lib/cn';
import { Button, Checkbox, Input, Label, Textarea } from '@/shared/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  CONDITIONAL_CONTROL_TYPES,
  CONDITIONAL_TYPE_OPTIONS,
  CONTROL_SPAN_OPTIONS,
  countMappingKeyUses,
  createCondition,
  createConditionalControl,
  formatControlType,
  getDefaultNextQuestionId,
  isChoiceControlType,
  updateOptions,
} from './builder-utils';
import type {
  Condition,
  ConditionalControl,
  FormControl,
  FormDefinition,
  FormSection,
  FormTypeOption,
  PropertiesTarget,
} from './types';
import { isConditionalType } from './types';

interface FormBuilderPropertiesProps {
  form: FormDefinition;
  target: PropertiesTarget;
  formTypeOptions: FormTypeOption[];
  selectedSection: FormSection | null;
  selectedControl: FormControl | null;
  onChangeTarget: (target: PropertiesTarget) => void;
  onUpdateForm: (updates: Partial<FormDefinition>) => void;
  onUpdateSection: (updates: Partial<FormSection>) => void;
  onUpdateControl: (updates: Partial<FormControl>) => void;
  onReplaceControl: (next: FormControl) => void;
}

type ActiveTab = 'settings' | 'validation' | 'appearance';

// Controls that are valid inside a condition branch
const CONDITIONAL_BRANCH_CONTROL_TYPES = CONDITIONAL_CONTROL_TYPES;

export default function FormBuilderProperties({
  form,
  target,
  formTypeOptions,
  selectedSection,
  selectedControl,
  onChangeTarget,
  onUpdateForm,
  onUpdateSection,
  onUpdateControl,
  onReplaceControl,
}: FormBuilderPropertiesProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('settings');
  const [expandedConditions, setExpandedConditions] = useState<Set<number>>(new Set());

  const isOptionControl = selectedControl
    ? isChoiceControlType(selectedControl.control_type)
    : false;

  const isConditionalField = selectedControl?.control_type === 'conditional_field';

  const controlOptions = selectedControl?.options || [];

  // Count every use of a mapping key across the WHOLE form — top-level controls,
  // group nested_controls and conditional-branch controls alike — so the panel
  // flags a collision wherever it is. Shares countMappingKeyUses with the
  // save-blocking check in validateFormDefinition so the two cannot drift.
  const hasDuplicateMappingKey = countMappingKeyUses(form, selectedControl?.mapping_key) > 1;
  const branchKeyIsDuplicate = (branchControl: ConditionalControl) =>
    countMappingKeyUses(form, branchControl.mapping_key) > 1;

  // Sentinel for "follow the natural order" since Radix Select disallows an
  // empty-string item value. Selecting it clears next_q_id (null), letting
  // syncFormControlLinks fall back to the next control in sequence.
  const NEXT_DEFAULT = '__default__';

  // Sentinel for "finish the form here". UI-only, like NEXT_DEFAULT — it is never
  // persisted. Selecting it sets end_form: true, from which syncFormControlLinks
  // derives the wire value (`next_q_id: ""`) that makes the runtimes show the
  // summary/save step instead of Next.
  const NEXT_END_FORM = '__end_form__';

  // All other questions in the form, as jump targets for the selected control.
  const nextQuestionTargets = selectedControl
    ? (form.sections ?? []).flatMap((section, sectionIndex) =>
        (section.controls ?? [])
          .filter((c) => c.q_id && c.q_id !== selectedControl.q_id)
          .map((c) => ({
            q_id: String(c.q_id),
            label: `${c.control_name?.trim() || formatControlType(c.control_type)} (#${c.q_id})`,
            sectionName: section.section_name?.trim() || `Section ${sectionIndex + 1}`,
          })),
      )
    : [];

  // Branching is not applicable to conditional_field (its next is computed from
  // the matched branch), so the selector is hidden for that type.
  const supportsNextQuestion =
    !!selectedControl && selectedControl.control_type !== 'conditional_field';

  // syncFormControlLinks resolves next_q_id to a concrete id even for
  // "default" flow, so a control whose next_q_id matches its natural next
  // control is still treated as Default in the selector.
  const defaultNextQId =
    selectedControl && target.type === 'control'
      ? getDefaultNextQuestionId(form.sections ?? [], target.sectionIndex, target.controlIndex)
      : null;
  const nextQuestionValue = selectedControl?.end_form
    ? NEXT_END_FORM
    : selectedControl?.next_q_id &&
        String(selectedControl.next_q_id) !== String(defaultNextQId ?? '')
      ? String(selectedControl.next_q_id)
      : NEXT_DEFAULT;

  // Picking a target clears end_form, and picking "End of form" clears the target,
  // so the two can never disagree (validateFormDefinition rejects that combination).
  const handleNextQuestionChange = (value: string) => {
    if (value === NEXT_END_FORM) {
      onUpdateControl({ next_q_id: null, end_form: true });
      return;
    }
    onUpdateControl({
      next_q_id: value === NEXT_DEFAULT ? null : value,
      end_form: false,
    });
  };

  const tabs: { id: ActiveTab; label: string }[] = [
    { id: 'settings', label: 'Settings' },
    { id: 'validation', label: 'Validation' },
    { id: 'appearance', label: 'Appearance' },
  ];

  // --- Condition helpers ---

  const updateConditions = (updater: (conditions: Condition[]) => Condition[]) => {
    if (!selectedControl) return;
    onUpdateControl({ conditions: updater(selectedControl.conditions ?? []) });
  };

  const addCondition = () => {
    updateConditions((conditions) => {
      const next = [...conditions, createCondition(conditions)];
      setExpandedConditions((prev) => new Set([...prev, next.length - 1]));
      return next;
    });
  };

  const removeCondition = (conditionIndex: number) => {
    updateConditions((conditions) => conditions.filter((_, i) => i !== conditionIndex));
    setExpandedConditions((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < conditionIndex) next.add(i);
        else if (i > conditionIndex) next.add(i - 1);
      });
      return next;
    });
  };

  const updateCondition = (conditionIndex: number, updates: Partial<Condition>) => {
    updateConditions((conditions) =>
      conditions.map((c, i) => (i === conditionIndex ? { ...c, ...updates } : c)),
    );
  };

  const addConditionalControl = (conditionIndex: number) => {
    updateConditions((conditions) =>
      conditions.map((c, i) =>
        i === conditionIndex
          ? { ...c, conditionalControls: [...c.conditionalControls, createConditionalControl()] }
          : c,
      ),
    );
  };

  const removeConditionalControl = (conditionIndex: number, controlIndex: number) => {
    updateConditions((conditions) =>
      conditions.map((c, i) =>
        i === conditionIndex
          ? {
              ...c,
              conditionalControls: c.conditionalControls.filter((_, ci) => ci !== controlIndex),
            }
          : c,
      ),
    );
  };

  const updateConditionalControl = (
    conditionIndex: number,
    controlIndex: number,
    updates: Partial<ConditionalControl>,
  ) => {
    updateConditions((conditions) =>
      conditions.map((c, i) =>
        i === conditionIndex
          ? {
              ...c,
              conditionalControls: c.conditionalControls.map((cc, ci) =>
                ci === controlIndex ? { ...cc, ...updates } : cc,
              ),
            }
          : c,
      ),
    );
  };

  const toggleConditionExpanded = (index: number) => {
    setExpandedConditions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className="border rounded-lg bg-white h-full flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-zinc-900">Properties</h3>
        <p className="text-xs text-zinc-500">
          {target.type === 'form'
            ? 'Edit form settings'
            : target.type === 'section'
              ? 'Edit section settings'
              : 'Edit field settings'}
        </p>
      </div>

      {/* Target selector */}
      <div className="flex gap-1 px-4 pb-2">
        <button
          type="button"
          onClick={() => onChangeTarget({ type: 'form' })}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'form'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
          )}
        >
          Form
        </button>
        <button
          type="button"
          onClick={() => {
            if (target.type === 'section' || target.type === 'control') {
              onChangeTarget({ type: 'section', sectionIndex: target.sectionIndex });
            }
          }}
          disabled={target.type === 'form'}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'section'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            target.type === 'form' && 'opacity-50 cursor-not-allowed',
          )}
        >
          Section
        </button>
        <button
          type="button"
          onClick={() => {
            if (target.type === 'control') {
              onChangeTarget(target);
            }
          }}
          disabled={target.type !== 'control'}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'control'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            target.type !== 'control' && 'opacity-50 cursor-not-allowed',
          )}
        >
          Field
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b mx-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-[#E84A2B] text-[#E84A2B]'
                : 'border-transparent text-zinc-500 hover:text-zinc-700',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* SETTINGS TAB */}
        {activeTab === 'settings' && target.type === 'form' && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Form name</Label>
              <Input
                value={form.name}
                onChange={(e) => onUpdateForm({ name: e.target.value })}
                placeholder="Enter form name"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => onUpdateForm({ description: e.target.value })}
                placeholder="Enter form description"
                rows={3}
                className="text-sm"
              />
            </div>
            {formTypeOptions.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Form type</Label>
                <Select
                  value={form.form_type_id != null ? String(form.form_type_id) : ''}
                  onValueChange={(v) => onUpdateForm({ form_type_id: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {formTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Layout</Label>
              <Select
                value={form.layout || 'Horizontal'}
                onValueChange={(v) => onUpdateForm({ layout: v })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Horizontal">Horizontal</SelectItem>
                  <SelectItem value="Vertical">Vertical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {activeTab === 'settings' && target.type === 'section' && selectedSection && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Section name</Label>
              <Input
                value={selectedSection.section_name}
                onChange={(e) => onUpdateSection({ section_name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={selectedSection.section_description ?? ''}
                onChange={(e) => onUpdateSection({ section_description: e.target.value })}
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="section-repeatable"
                checked={Boolean(selectedSection.is_repeatable)}
                onCheckedChange={(checked) =>
                  onUpdateSection({
                    is_repeatable: Boolean(checked),
                    max_repeat: checked
                      ? (selectedSection.max_repeat ?? 100)
                      : selectedSection.max_repeat,
                  })
                }
              />
              <Label htmlFor="section-repeatable" className="text-xs">
                Repeatable section
              </Label>
            </div>
            {selectedSection.is_repeatable && (
              <div className="space-y-1.5">
                <Label className="text-xs">Max repeats</Label>
                <Input
                  type="number"
                  value={selectedSection.max_repeat ?? 100}
                  onChange={(e) => {
                    const parsed = Number(e.target.value);
                    const value = Number.isFinite(parsed)
                      ? Math.min(100, Math.max(1, parsed))
                      : 100;
                    onUpdateSection({ max_repeat: value });
                  }}
                  min={1}
                  max={100}
                  className="h-8 text-sm"
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'settings' && target.type === 'control' && selectedControl && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Field type</Label>
              <Input
                value={formatControlType(selectedControl.control_type)}
                disabled
                className="h-8 text-sm bg-zinc-50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Label</Label>
              <Input
                value={selectedControl.control_name}
                onChange={(e) => onUpdateControl({ control_name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={selectedControl.control_description}
                onChange={(e) => onUpdateControl({ control_description: e.target.value })}
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Help text</Label>
              <Input
                value={selectedControl.help_text ?? ''}
                onChange={(e) => onUpdateControl({ help_text: e.target.value })}
                className="h-8 text-sm"
                placeholder="Help text shown to user"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mapping key</Label>
              <Input
                value={selectedControl.mapping_key ?? ''}
                onChange={(e) => onUpdateControl({ mapping_key: e.target.value })}
                className={cn(
                  'h-8 text-sm',
                  hasDuplicateMappingKey && 'border-red-500 focus-visible:ring-red-500',
                )}
                placeholder="incident_reference"
                aria-invalid={hasDuplicateMappingKey}
              />
              <p
                className={cn(
                  'text-[11px]',
                  hasDuplicateMappingKey ? 'text-red-600' : 'text-muted-foreground',
                )}
              >
                {hasDuplicateMappingKey
                  ? 'Mapping key must be unique in this form.'
                  : 'Stable key used to map this answer to CORE. Optional, but must be unique.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="control-required"
                checked={selectedControl.required}
                onCheckedChange={(checked) =>
                  onUpdateControl({
                    required: Boolean(checked),
                    // Required and Read only are mutually exclusive (CWL-305): a
                    // field the user can't edit can't also block the form on
                    // being left blank.
                    read_only: checked ? false : selectedControl.read_only,
                  })
                }
              />
              <Label htmlFor="control-required" className="text-xs">
                Required
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="control-hidden"
                checked={selectedControl.hidden ?? false}
                onCheckedChange={(checked) => onUpdateControl({ hidden: Boolean(checked) })}
              />
              <Label htmlFor="control-hidden" className="text-xs">
                Hidden
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="control-read-only"
                checked={selectedControl.read_only ?? false}
                onCheckedChange={(checked) =>
                  onUpdateControl({
                    read_only: Boolean(checked),
                    required: checked ? false : selectedControl.required,
                  })
                }
              />
              <Label htmlFor="control-read-only" className="text-xs">
                Read only
              </Label>
            </div>

            {/* Min/Max */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min</Label>
                <Input
                  value={selectedControl.min}
                  onChange={(e) => onUpdateControl({ min: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max</Label>
                <Input
                  value={selectedControl.max}
                  onChange={(e) => onUpdateControl({ max: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Next question (branching) — hidden for conditional_field */}
            {supportsNextQuestion && (
              <div className="space-y-1.5">
                <Label htmlFor="control-next-question" className="text-xs">
                  Next question
                </Label>
                <Select value={nextQuestionValue} onValueChange={handleNextQuestionChange}>
                  <SelectTrigger id="control-next-question" className="h-8 text-sm">
                    <SelectValue placeholder="Default (next in order)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NEXT_DEFAULT}>Default (next in order)</SelectItem>
                    {/* Offered on every question, including the last one in the form (SC-6545).
                        It is not a no-op there: end_form persists as `next_q_id: ""`, the sentinel
                        mobile compares against for the summary, where the natural end persists
                        null. Hiding it also left an author-set end_form with no item to render,
                        so the trigger came up blank once a question became last. */}
                    <SelectItem value={NEXT_END_FORM}>End of form (go to summary)</SelectItem>
                    {nextQuestionTargets.map((t) => (
                      <SelectItem key={t.q_id} value={t.q_id}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Where the form goes after this question. Default follows the order of questions,
                  which on the final question already finishes the form. End of form finishes here
                  and goes to the summary, skipping any later questions.
                </p>
              </div>
            )}

            {/* Options editor for choice types */}
            {isOptionControl && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Options</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[11px] px-2"
                    onClick={() => {
                      const next = updateOptions(selectedControl, (opts) => [
                        ...opts,
                        { option: '' },
                      ]);
                      onReplaceControl(next);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                {controlOptions.map((option, index) => (
                  <div key={`option-${index}`} className="flex items-center gap-1">
                    <Input
                      value={option.option}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) => {
                        const next = updateOptions(selectedControl, (opts) =>
                          opts.map((item, idx) =>
                            idx === index ? { ...item, option: e.target.value } : item,
                          ),
                        );
                        onReplaceControl(next);
                      }}
                      className="h-7 text-xs flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = updateOptions(selectedControl, (opts) =>
                          opts.filter((_, idx) => idx !== index),
                        );
                        onReplaceControl(next);
                      }}
                      className="p-1 rounded hover:bg-zinc-100"
                    >
                      <Trash2 className="w-3 h-3 text-zinc-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Conditional field routing editor */}
            {isConditionalField && (
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-xs font-semibold">Conditional routing</Label>

                {/* Widget this question renders as. Mobile switches on this value and
                    renders nothing for anything outside the set (SC-6511). */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Conditional type</Label>
                  <Select
                    value={selectedControl.conditional_type || ''}
                    onValueChange={(v) => {
                      // Radix hands back a plain string; narrow it rather than cast.
                      if (isConditionalType(v)) onUpdateControl({ conditional_type: v });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select conditional type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITIONAL_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    How this question is answered. The conditions below are its options.
                  </p>
                </div>

                {/* Conditions list */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Conditions</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[11px] px-2"
                      onClick={addCondition}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add condition
                    </Button>
                  </div>

                  {(selectedControl.conditions ?? []).length === 0 && (
                    <p className="text-[11px] text-zinc-400 py-2 text-center">
                      No conditions yet. Add one above.
                    </p>
                  )}

                  {(selectedControl.conditions ?? []).map((condition, conditionIndex) => {
                    const isExpanded = expandedConditions.has(conditionIndex);
                    return (
                      <div
                        key={`condition-${conditionIndex}`}
                        className="border rounded-md overflow-hidden"
                      >
                        {/* Condition header */}
                        <div className="flex items-center gap-1 px-2 py-1.5 bg-zinc-50">
                          <button
                            type="button"
                            onClick={() => toggleConditionExpanded(conditionIndex)}
                            className="flex items-center gap-1 flex-1 min-w-0 text-left"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-3 h-3 text-zinc-400 shrink-0" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-zinc-400 shrink-0" />
                            )}
                            <span className="text-xs font-medium text-zinc-700 truncate">
                              {condition.condition_name?.trim()
                                ? condition.condition_name
                                : `Condition ${conditionIndex + 1}`}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => removeCondition(conditionIndex)}
                            className="p-0.5 rounded hover:bg-zinc-200 shrink-0"
                            title="Remove condition"
                          >
                            <Trash2 className="w-3 h-3 text-zinc-400" />
                          </button>
                        </div>

                        {/* Condition body */}
                        {isExpanded && (
                          <div className="px-3 py-3 space-y-3">
                            {/* Condition name (the option value that triggers this branch) */}
                            <div className="space-y-1.5">
                              <Label className="text-xs">When answer is</Label>
                              <Input
                                value={condition.condition_name}
                                onChange={(e) =>
                                  updateCondition(conditionIndex, {
                                    condition_name: e.target.value,
                                  })
                                }
                                placeholder="Option value e.g. Yes"
                                className="h-7 text-xs"
                              />
                            </div>

                            {/* Order */}
                            <div className="space-y-1.5">
                              <Label className="text-xs">Order</Label>
                              <Input
                                type="number"
                                value={condition.condition_order}
                                onChange={(e) =>
                                  updateCondition(conditionIndex, {
                                    condition_order: Number(e.target.value),
                                  })
                                }
                                className="h-7 text-xs"
                                min={0}
                              />
                            </div>

                            {/* Next question after this branch */}
                            <div className="space-y-1.5">
                              <Label className="text-xs">Go to question after branch</Label>
                              <Select
                                value={
                                  condition.end_form
                                    ? NEXT_END_FORM
                                    : condition.next_q_id
                                      ? String(condition.next_q_id)
                                      : NEXT_DEFAULT
                                }
                                onValueChange={(v) =>
                                  updateCondition(
                                    conditionIndex,
                                    v === NEXT_END_FORM
                                      ? { next_q_id: null, end_form: true }
                                      : {
                                          next_q_id: v === NEXT_DEFAULT ? null : v,
                                          end_form: false,
                                        },
                                  )
                                }
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Default (next in order)" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NEXT_DEFAULT}>
                                    Default (next in order)
                                  </SelectItem>
                                  <SelectItem value={NEXT_END_FORM}>
                                    End of form (go to summary)
                                  </SelectItem>
                                  {nextQuestionTargets.map((t) => (
                                    <SelectItem key={t.q_id} value={t.q_id}>
                                      {t.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {condition.end_form && (
                                <p className="text-[11px] text-muted-foreground">
                                  Choosing this answer ends the form and goes to the summary,
                                  skipping any later questions.
                                </p>
                              )}
                            </div>

                            {/* Conditional controls in this branch */}
                            <div className="space-y-2 pt-1 border-t">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Branch fields</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-5 text-[10px] px-1.5"
                                  onClick={() => addConditionalControl(conditionIndex)}
                                >
                                  <Plus className="w-2.5 h-2.5 mr-0.5" />
                                  Add field
                                </Button>
                              </div>

                              {condition.conditionalControls.length === 0 && (
                                <p className="text-[10px] text-zinc-400 text-center py-1">
                                  No fields. Add one to show when this condition is met.
                                </p>
                              )}

                              {condition.conditionalControls.map((cc, ccIndex) => (
                                <div
                                  key={`cc-${conditionIndex}-${ccIndex}`}
                                  className="border rounded p-2 space-y-2 bg-zinc-50"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-medium text-zinc-600">
                                      Field {ccIndex + 1}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeConditionalControl(conditionIndex, ccIndex)
                                      }
                                      className="p-0.5 rounded hover:bg-zinc-200"
                                      title="Remove field"
                                    >
                                      <Trash2 className="w-2.5 h-2.5 text-zinc-400" />
                                    </button>
                                  </div>

                                  <div className="space-y-1">
                                    <Label className="text-[10px]">Label</Label>
                                    <Input
                                      value={cc.control_name}
                                      onChange={(e) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          control_name: e.target.value,
                                        })
                                      }
                                      placeholder="Field label"
                                      className="h-6 text-[11px]"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <Label className="text-[10px]">Mapping key</Label>
                                    <Input
                                      value={cc.mapping_key ?? ''}
                                      onChange={(e) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          mapping_key: e.target.value,
                                        })
                                      }
                                      placeholder="pressure_material"
                                      className={cn(
                                        'h-6 text-[11px]',
                                        branchKeyIsDuplicate(cc) &&
                                          'border-red-500 focus-visible:ring-red-500',
                                      )}
                                      aria-invalid={branchKeyIsDuplicate(cc)}
                                    />
                                    <p
                                      className={cn(
                                        'text-[10px]',
                                        branchKeyIsDuplicate(cc)
                                          ? 'text-red-600'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {branchKeyIsDuplicate(cc)
                                        ? 'Already used elsewhere in this form.'
                                        : 'Stable key CORE maps this answer to.'}
                                    </p>
                                  </div>

                                  <div className="space-y-1">
                                    <Label className="text-[10px]">Type</Label>
                                    <Select
                                      value={cc.control_type}
                                      onValueChange={(v) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          control_type: v,
                                        })
                                      }
                                    >
                                      <SelectTrigger className="h-6 text-[11px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CONDITIONAL_BRANCH_CONTROL_TYPES.map((t) => (
                                          <SelectItem key={t.value} value={t.value}>
                                            {t.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {/* Choice-type branch fields are drawn from their
                                      options on mobile, so without any option the
                                      field renders as an empty space (SC-6511). */}
                                  {isChoiceControlType(cc.control_type) && (
                                    <div className="space-y-1">
                                      <div className="flex items-center justify-between">
                                        <Label className="text-[10px]">Options</Label>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-5 text-[10px] px-1.5"
                                          onClick={() =>
                                            updateConditionalControl(conditionIndex, ccIndex, {
                                              options: [...(cc.options ?? []), { option: '' }],
                                            })
                                          }
                                        >
                                          <Plus className="w-2.5 h-2.5 mr-0.5" />
                                          Add option
                                        </Button>
                                      </div>
                                      {(cc.options ?? []).length === 0 && (
                                        <p className="text-[10px] text-red-600">
                                          Add at least one option, or this field shows as blank on
                                          mobile.
                                        </p>
                                      )}
                                      {(cc.options ?? []).map((option, optIndex) => (
                                        <div
                                          key={`cc-opt-${conditionIndex}-${ccIndex}-${optIndex}`}
                                          className="flex items-center gap-1"
                                        >
                                          <Input
                                            value={option.option}
                                            placeholder={`Option ${optIndex + 1}`}
                                            onChange={(e) =>
                                              updateConditionalControl(conditionIndex, ccIndex, {
                                                options: (cc.options ?? []).map((item, idx) =>
                                                  idx === optIndex
                                                    ? { ...item, option: e.target.value }
                                                    : item,
                                                ),
                                              })
                                            }
                                            className="h-6 text-[11px] flex-1"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateConditionalControl(conditionIndex, ccIndex, {
                                                options: (cc.options ?? []).filter(
                                                  (_, idx) => idx !== optIndex,
                                                ),
                                              })
                                            }
                                            className="p-0.5 rounded hover:bg-zinc-200"
                                            title="Remove option"
                                          >
                                            <Trash2 className="w-2.5 h-2.5 text-zinc-400" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <div className="flex items-center gap-1.5">
                                    <Checkbox
                                      id={`cc-required-${conditionIndex}-${ccIndex}`}
                                      checked={cc.required}
                                      onCheckedChange={(checked) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          required: Boolean(checked),
                                          // Mutually exclusive with Read only (CWL-305).
                                          read_only: checked ? false : cc.read_only,
                                        })
                                      }
                                    />
                                    <Label
                                      htmlFor={`cc-required-${conditionIndex}-${ccIndex}`}
                                      className="text-[10px]"
                                    >
                                      Required
                                    </Label>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Checkbox
                                      id={`cc-hidden-${conditionIndex}-${ccIndex}`}
                                      checked={cc.hidden ?? false}
                                      onCheckedChange={(checked) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          hidden: Boolean(checked),
                                        })
                                      }
                                    />
                                    <Label
                                      htmlFor={`cc-hidden-${conditionIndex}-${ccIndex}`}
                                      className="text-[10px]"
                                    >
                                      Hidden
                                    </Label>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Checkbox
                                      id={`cc-read-only-${conditionIndex}-${ccIndex}`}
                                      checked={cc.read_only ?? false}
                                      onCheckedChange={(checked) =>
                                        updateConditionalControl(conditionIndex, ccIndex, {
                                          read_only: Boolean(checked),
                                          required: checked ? false : cc.required,
                                        })
                                      }
                                    />
                                    <Label
                                      htmlFor={`cc-read-only-${conditionIndex}-${ccIndex}`}
                                      className="text-[10px]"
                                    >
                                      Read only
                                    </Label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* VALIDATION TAB */}
        {activeTab === 'validation' && target.type === 'control' && selectedControl && (
          <>
            <div className="flex items-center gap-2">
              <Checkbox
                id="val-required"
                checked={selectedControl.required}
                onCheckedChange={(checked) => onUpdateControl({ required: Boolean(checked) })}
              />
              <Label htmlFor="val-required" className="text-xs">
                Required field
              </Label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min length</Label>
                <Input
                  value={selectedControl.min}
                  onChange={(e) => onUpdateControl({ min: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max length</Label>
                <Input
                  value={selectedControl.max}
                  onChange={(e) => onUpdateControl({ max: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            {(selectedControl.validations || []).map((validation: any, index: number) => (
              <div key={`val-${index}`} className="flex items-center gap-2">
                <Input
                  value={validation.name || ''}
                  onChange={(e) => {
                    const next = (selectedControl.validations || []).map(
                      (item: any, idx: number) =>
                        idx === index ? { ...item, name: e.target.value } : item,
                    );
                    onUpdateControl({ validations: next });
                  }}
                  placeholder="Rule name"
                  className="h-7 text-xs flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = (selectedControl.validations || []).filter(
                      (_: any, idx: number) => idx !== index,
                    );
                    onUpdateControl({ validations: next });
                  }}
                  className="p-1 rounded hover:bg-zinc-100"
                >
                  <Trash2 className="w-3 h-3 text-zinc-400" />
                </button>
              </div>
            ))}
          </>
        )}

        {activeTab === 'validation' && target.type !== 'control' && (
          <p className="text-xs text-zinc-400 text-center py-4">
            Select a field to edit validation rules.
          </p>
        )}

        {/* APPEARANCE TAB */}
        {activeTab === 'appearance' && target.type === 'control' && selectedControl && (
          <div className="space-y-1.5">
            <Label className="text-xs">Control width</Label>
            <Select
              value={selectedControl.control_span_length || '12'}
              onValueChange={(v) => onUpdateControl({ control_span_length: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTROL_SPAN_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {activeTab === 'appearance' && target.type !== 'control' && (
          <p className="text-xs text-zinc-400 text-center py-4">
            Select a field to edit appearance settings.
          </p>
        )}
      </div>
    </div>
  );
}
